/**
 * 偵測取樣影像中的「淺色矩形區域」（如托運單、標籤、出貨單）
 *
 * 純函數，不依賴 React / DOM / Canvas。
 * 輸入已裁切 + 縮放到 SAMPLE_W × SAMPLE_H 的 RGBA 像素陣列。
 *
 * @param {Uint8Array|Uint8ClampedArray|Buffer} pixels - RGBA 像素資料 (length = w*h*4)
 * @param {number} width  - 取樣寬度（預設 240）
 * @param {number} height - 取樣高度（預設 150）
 * @param {Object} options
 * @param {number} options.minLightness  - 亮度下限 0~255（預設 'auto'）
 *   'auto' 時使用直方圖 P80 自適應閾值（硬地板 150），適應不同光源環境
 *   傳入數字時使用固定閾值（向下相容）
 * @param {number} options.maxColorRange - RGB 色差上限（預設 35）
 * @param {number} options.minAreaRatio  - 最小面積佔比（預設 0.02）
 * @param {number} options.cropMargin    - 裁切邊距比例（預設 0.175 = 中央 65%）
 * @returns {{ detected: boolean, region: object|null, debug: object }}
 */
export function detectWhiteRegion(pixels, width, height, options = {}) {
  const {
    minLightness = 'auto',
    maxColorRange = 35,
    minAreaRatio = 0.02,
    cropMargin = 0.175,
  } = options;

  const totalPixels = width * height;

  // ─── Step 0: 自適應亮度閾值 ───
  // 用直方圖找 P80（80% 像素低於此亮度），單據應在最亮的 20% 中。
  // 硬地板 150 防止全暗場景誤判。傳入固定數字時跳過自適應。
  let effectiveMinLightness;
  if (minLightness === 'auto') {
    const HARD_FLOOR = 150;
    const histogram = new Uint32Array(256);
    for (let i = 0; i < totalPixels; i++) {
      const r = pixels[i * 4], g = pixels[i * 4 + 1], b = pixels[i * 4 + 2];
      histogram[(Math.max(r, g, b) + Math.min(r, g, b)) >> 1]++;
    }
    const target = Math.floor(totalPixels * 0.8);
    let cumulative = 0;
    let p80 = 255;
    for (let j = 0; j < 256; j++) {
      cumulative += histogram[j];
      if (cumulative >= target) { p80 = j; break; }
    }
    effectiveMinLightness = Math.max(HARD_FLOOR, p80);
  } else {
    effectiveMinLightness = minLightness;
  }

  // ─── Step 1: 二值化遮罩 ───
  const mask = new Uint8Array(totalPixels);
  for (let i = 0; i < totalPixels; i++) {
    const r = pixels[i * 4];
    const g = pixels[i * 4 + 1];
    const b = pixels[i * 4 + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;
    const range = max - min;
    if (lightness >= effectiveMinLightness && range <= maxColorRange) {
      mask[i] = 1;
    }
  }

  // ─── Step 2: Flood-fill 找最大連通區塊 ───
  const blobId = new Uint16Array(totalPixels);
  let currentBlobId = 0;
  let bestBlob = null;
  let bestSize = 0;
  let bestBlobId = 0;

  for (let i = 0; i < totalPixels; i++) {
    if (mask[i] && !blobId[i]) {
      currentBlobId++;
      const queue = [i];
      blobId[i] = currentBlobId;
      let minX = width, maxX = 0, minY = height, maxY = 0;
      let count = 0;

      while (queue.length > 0) {
        const idx = queue.pop();
        const x = idx % width;
        const y = Math.floor(idx / width);
        count++;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;

        const neighbors = [];
        if (x > 0) neighbors.push(idx - 1);
        if (x < width - 1) neighbors.push(idx + 1);
        if (y > 0) neighbors.push(idx - width);
        if (y < height - 1) neighbors.push(idx + width);

        for (const n of neighbors) {
          if (mask[n] && !blobId[n]) {
            blobId[n] = currentBlobId;
            queue.push(n);
          }
        }
      }

      if (count > bestSize) {
        bestSize = count;
        bestBlobId = currentBlobId;
        bestBlob = { minX, maxX, minY, maxY, count };
      }
    }
  }

  const lightPixelCount = mask.reduce((sum, v) => sum + v, 0);
  const lightRatio = lightPixelCount / totalPixels;

  // ─── 硬性前提 ───
  if (!bestBlob || bestBlob.count / totalPixels < minAreaRatio) {
    return {
      detected: false,
      region: null,
      debug: {
        reason: 'no_light_region',
        threshold: effectiveMinLightness,
        lightPixels: pct(lightRatio),
        blobArea: bestBlob ? pct(bestBlob.count / totalPixels) : '0%',
        scores: null,
        debugRegion: null,
      },
    };
  }

  const blobW = bestBlob.maxX - bestBlob.minX + 1;
  const blobH = bestBlob.maxY - bestBlob.minY + 1;
  const fillRatio = bestBlob.count / (blobW * blobH);

  // ─── Step 3: 內容偵測（interior gradient method）───
  // 在 blob 內部的像素中，計算有多少像素附近有顯著亮度梯度。
  // 白紙旁邊有文字/條碼時，blob 像素的鄰居亮度差異大 → 高梯度。
  // 純白牆面幾乎無梯度。
  //
  // 只看 blob「內部」像素（4-鄰域全是 blob），排除 blob 邊界（邊界的
  // 高梯度來自白紙→背景的過渡，不是文字信號）。
  const GRADIENT_THRESHOLD = 20;
  let highGradCount = 0;
  let interiorBlobCount = 0;

  for (let y = bestBlob.minY; y <= bestBlob.maxY; y++) {
    for (let x = bestBlob.minX; x <= bestBlob.maxX; x++) {
      const idx = y * width + x;
      if (blobId[idx] !== bestBlobId) continue;

      // 必須是 interior blob pixel（4-鄰域全是 blob）
      if (x <= 0 || blobId[idx - 1] !== bestBlobId) continue;
      if (x >= width - 1 || blobId[idx + 1] !== bestBlobId) continue;
      if (y <= 0 || blobId[idx - width] !== bestBlobId) continue;
      if (y >= height - 1 || blobId[idx + width] !== bestBlobId) continue;

      interiorBlobCount++;

      // 計算梯度（用鄰居像素，不限 blob）
      const lum = (pixels[idx * 4] + pixels[idx * 4 + 1] + pixels[idx * 4 + 2]) / 3;
      const lumL = (pixels[(idx - 1) * 4] + pixels[(idx - 1) * 4 + 1] + pixels[(idx - 1) * 4 + 2]) / 3;
      const lumR = (pixels[(idx + 1) * 4] + pixels[(idx + 1) * 4 + 1] + pixels[(idx + 1) * 4 + 2]) / 3;
      const lumU = (pixels[(idx - width) * 4] + pixels[(idx - width) * 4 + 1] + pixels[(idx - width) * 4 + 2]) / 3;
      const lumD = (pixels[(idx + width) * 4] + pixels[(idx + width) * 4 + 1] + pixels[(idx + width) * 4 + 2]) / 3;
      const grad = Math.abs(lumR - lumL) + Math.abs(lumD - lumU);

      if (grad > GRADIENT_THRESHOLD) highGradCount++;
    }
  }
  const darkRatio = interiorBlobCount > 0 ? highGradCount / interiorBlobCount : 0;

  // ─── Step 4: 邊緣直線性（PCA 殘差法）───
  const edgePoints = { top: [], bottom: [], left: [], right: [] };

  for (let x = bestBlob.minX; x <= bestBlob.maxX; x++) {
    let topY = -1, bottomY = -1;
    for (let y = bestBlob.minY; y <= bestBlob.maxY; y++) {
      if (blobId[y * width + x] === bestBlobId) {
        if (topY === -1) topY = y;
        bottomY = y;
      }
    }
    if (topY !== -1) {
      edgePoints.top.push({ x, y: topY });
      edgePoints.bottom.push({ x, y: bottomY });
    }
  }
  for (let y = bestBlob.minY; y <= bestBlob.maxY; y++) {
    let leftX = -1, rightX = -1;
    for (let x = bestBlob.minX; x <= bestBlob.maxX; x++) {
      if (blobId[y * width + x] === bestBlobId) {
        if (leftX === -1) leftX = x;
        rightX = x;
      }
    }
    if (leftX !== -1) {
      edgePoints.left.push({ x: leftX, y });
      edgePoints.right.push({ x: rightX, y });
    }
  }

  const stdTop = edgeResidualStd(edgePoints.top);
  const stdBottom = edgeResidualStd(edgePoints.bottom);
  const stdLeft = edgeResidualStd(edgePoints.left);
  const stdRight = edgeResidualStd(edgePoints.right);

  const edgeStds = [stdTop, stdBottom, stdLeft, stdRight]
    .map(s => Math.min(s, 99))
    .sort((a, b) => a - b);
  const edgeAvgStd = (edgeStds[0] + edgeStds[1] + edgeStds[2]) / 3;

  // ─── Step 5: 評分系統（雙條件 AND）───
  const WEIGHTS = { area: 15, fill: 20, dark: 25, edge: 30, light: 10 };

  const scores = {
    area:  ramp(bestBlob.count / totalPixels, 0.02, 0.08),
    fill:  ramp(fillRatio, 0.30, 0.65),
    // 內容梯度密度：2% 以下不計分（純白牆），2~8% 爬升，>8% 滿分
    dark:  ramp(darkRatio, 0.02, 0.08),
    edge:  ramp(5 - edgeAvgStd, 1, 4),
    light: ramp(lightRatio, 0.03, 0.10),
  };

  const shapeScore = Math.round(
    scores.area  * WEIGHTS.area +
    scores.fill  * WEIGHTS.fill +
    scores.edge  * WEIGHTS.edge +
    scores.light * WEIGHTS.light
  );
  const contentScore = Math.round(scores.dark * WEIGHTS.dark);
  const totalScore = shapeScore + contentScore;

  const SHAPE_MAX = WEIGHTS.area + WEIGHTS.fill + WEIGHTS.edge + WEIGHTS.light;
  const CONTENT_MAX = WEIGHTS.dark;
  const SHAPE_THRESHOLD = 35;
  const CONTENT_THRESHOLD = 8;

  const detected = shapeScore >= SHAPE_THRESHOLD && contentScore >= CONTENT_THRESHOLD;

  // ─── 座標映射（取樣座標 → 全畫面比例座標）───
  const margin = cropMargin;
  const debugRegion = {
    x: margin + (bestBlob.minX + bestBlob.maxX) / 2 / width * (1 - 2 * margin),
    y: margin + (bestBlob.minY + bestBlob.maxY) / 2 / height * (1 - 2 * margin),
    w: blobW / width * (1 - 2 * margin),
    h: blobH / height * (1 - 2 * margin),
  };

  const debugScores = {
    area:  `${(scores.area  * WEIGHTS.area).toFixed(0)}/${WEIGHTS.area}`,
    fill:  `${(scores.fill  * WEIGHTS.fill).toFixed(0)}/${WEIGHTS.fill}`,
    dark:  `${(scores.dark  * WEIGHTS.dark).toFixed(0)}/${WEIGHTS.dark}`,
    edge:  `${(scores.edge  * WEIGHTS.edge).toFixed(0)}/${WEIGHTS.edge}`,
    light: `${(scores.light * WEIGHTS.light).toFixed(0)}/${WEIGHTS.light}`,
    shape: `${shapeScore}/${SHAPE_MAX}`,
    content: `${contentScore}/${CONTENT_MAX}`,
    total: `${totalScore}/100`,
  };

  return {
    detected,
    region: detected ? debugRegion : null,
    debug: {
      threshold: effectiveMinLightness,
      lightPixels: pct(lightRatio),
      blobArea: pct(bestBlob.count / totalPixels),
      fillRatio: pct(fillRatio),
      darkRatio: `${pct(darkRatio)} (${highGradCount}梯/${interiorBlobCount}內)`,
      edgeBest3Avg: `${edgeAvgStd.toFixed(1)}px`,
      edgeDetail: `T:${stdTop.toFixed(1)} B:${stdBottom.toFixed(1)} L:${stdLeft.toFixed(1)} R:${stdRight.toFixed(1)} (去最差)`,
      scores: debugScores,
      debugRegion,
    },
  };
}

// ─── 工具函數 ───

function pct(ratio) {
  return `${(ratio * 100).toFixed(1)}%`;
}

function ramp(val, lo, hi) {
  return Math.max(0, Math.min(1, (val - lo) / (hi - lo)));
}

function edgeResidualStd(points) {
  if (points.length < 3) return Infinity;
  const n = points.length;
  let sumX = 0, sumY = 0;
  for (let i = 0; i < n; i++) { sumX += points[i].x; sumY += points[i].y; }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let cxx = 0, cyy = 0, cxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = points[i].x - meanX;
    const dy = points[i].y - meanY;
    cxx += dx * dx;
    cyy += dy * dy;
    cxy += dx * dy;
  }

  const theta = 0.5 * Math.atan2(2 * cxy, cxx - cyy);
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);

  let ssResidual = 0;
  for (let i = 0; i < n; i++) {
    const dx = points[i].x - meanX;
    const dy = points[i].y - meanY;
    const residual = -dx * sin + dy * cos;
    ssResidual += residual * residual;
  }

  return Math.sqrt(ssResidual / n);
}
